import { ActivityLogTab, DocumentsTab } from '@/domains/jobs/components/ActivityLogDocumentsTabs';
import { DFETab } from '@/domains/jobs/components/DFETab';
import { DataCaptureTab } from '@/domains/jobs/components/DataCaptureTab';
import { queryKeys } from '@/lib/query-client';
import { PageHeader } from '@/shared/components/PageHeader';
import { useQueryClient } from '@tanstack/react-query';
import {
  Briefcase,
  Camera,
  FileText,
  History,
  Layers,
  MessageSquare,
  PoundSterling,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useJobData } from '../hooks/useJobData';
import { ConversationThread } from './ConversationThread';
import { CustomerInfoCard } from './CustomerInfoCard';
import { JobDetailsCard } from './JobDetailsCard';
import { MapSection } from './MapSection';
import { MilestonesCard } from './MilestonesCard';
import { PermitCard } from './PermitCard';
import { StatusBar } from './StatusBar';
import { TasksCard } from './TasksCard';

interface JobDetailsViewProps {
  jobId: string;
  onBack: () => void;
}

export function JobDetailsView({ jobId, onBack }: JobDetailsViewProps) {
  const queryClient = useQueryClient();
  const { jobData, jobStatus, conversationThread, isLoading } = useJobData(jobId);
  const [activeTab, setActiveTab] = useState<
    'overview' | 'activity-log' | 'documents' | 'messages' | 'data-capture' | 'dfe'
  >('overview');
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');

  const handleSendResponse = () => {
    console.log('Sending response:', responseMessage);
    setResponseMessage('');
    setShowResponseForm(false);
  };

  const hasConversation = conversationThread && conversationThread.length > 0;

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.jobs.detail(jobId),
    });
  }, [jobId]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E84A2B] mx-auto mb-4" />
          <p className="text-muted-foreground text-sm">Loading job details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background overflow-hidden">
      <PageHeader
        title={jobData.title}
        subtitle={`Job #${jobData.id}`}
        icon={<Briefcase className="w-7 h-7 text-orange-600 dark:text-orange-400" />}
      />

      {/* Status Bar */}
      <div className="px-6 pt-6 border-b border-border pb-4">
        <StatusBar jobData={jobData} />
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="px-6">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'overview'
                  ? 'border-[#E84A2B] text-[#E84A2B]'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Overview
              </div>
            </button>
            <button
              onClick={() => setActiveTab('activity-log')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'activity-log'
                  ? 'border-[#E84A2B] text-[#E84A2B]'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <History className="w-4 h-4" />
                Activity Log
              </div>
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'documents'
                  ? 'border-[#E84A2B] text-[#E84A2B]'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Documents
              </div>
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'messages'
                  ? 'border-[#E84A2B] text-[#E84A2B]'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Messages
                {hasConversation && (
                  <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-xs font-medium rounded-full bg-[#E84A2B] text-white">
                    {conversationThread.length}
                  </span>
                )}
                {jobData.hasResponse && <div className="w-2 h-2 rounded-full bg-green-500" />}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('data-capture')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'data-capture'
                  ? 'border-[#E84A2B] text-[#E84A2B]'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Data Capture
              </div>
            </button>
            {!jobData.validated && (
              <button
                onClick={() => setActiveTab('dfe')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'dfe'
                    ? 'border-[#E84A2B] text-[#E84A2B]'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <div className="flex items-center gap-2">
                  <PoundSterling className="w-4 h-4" />
                  DFE
                </div>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-6 py-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Top Grid: Details + Map */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <JobDetailsCard jobData={jobData} />
                <MapSection address={jobData.address} coordinates={jobData.coordinates} />
              </div>

              {/* Middle Grid: Tasks + Customer */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <TasksCard tasks={jobData.tasks} jobId={jobId} />
                <CustomerInfoCard
                  clientName={jobData.client}
                  contractName={jobData.contractName}
                  notes={typeof jobData.notes === 'string' ? jobData.notes : undefined}
                />
              </div>

              {/* Permit & Reinstatement */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <PermitCard jobData={jobData} />
              </div>

              {/* Milestones */}
              {jobData.milestones && <MilestonesCard milestones={jobData.milestones} />}
            </div>
          )}

          {activeTab === 'activity-log' && <ActivityLogTab jobId={jobId} jobData={jobData} />}

          {activeTab === 'documents' && <DocumentsTab jobId={jobId} />}

          {activeTab === 'messages' &&
            (hasConversation ? (
              <ConversationThread
                conversationThread={conversationThread}
                jobStatus={jobStatus}
                showResponseForm={showResponseForm}
                setShowResponseForm={setShowResponseForm}
                responseMessage={responseMessage}
                setResponseMessage={setResponseMessage}
                onSendResponse={handleSendResponse}
              />
            ) : (
              <div className="max-w-4xl mx-auto">
                <div className="bg-card border border-border rounded-lg p-12 text-center">
                  <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">No messages yet</h3>
                  <p className="text-sm text-muted-foreground">
                    Communication history will appear here when you request more information,
                    reject, or receive replies.
                  </p>
                </div>
              </div>
            ))}

          {activeTab === 'data-capture' && (
            <DataCaptureTab
              jobId={jobId}
              isValidated={jobData.validated}
              readOnly={jobData.validated}
            />
          )}

          {activeTab === 'dfe' && <DFETab jobId={jobId} />}
        </div>
      </div>
    </div>
  );
}
